/** API / domain health for planned vs actual comparisons. */
export enum BudgetHealthStatus {
  Healthy = "healthy",
  Warning = "warning",
  Critical = "critical",
}

/** List/overview context for branch vs parish. */
export enum BudgetListContext {
  Branch = "branch",
  Parish = "parish",
}

/** Optional time bucketing for budget overview. */
export enum BudgetOverviewGranularity {
  Daily = "daily",
  Weekly = "weekly",
  Monthly = "monthly",
}

/** Query string enum values accepted by the budgeting API (match the Prisma `BudgetStatus` enum). */
export enum BudgetListStatusQuery {
  Draft = "DRAFT",
  ReadyForReview = "READY_FOR_REVIEW",
  InReview = "IN_REVIEW",
  ChangesRequested = "CHANGES_REQUESTED",
  Approved = "APPROVED",
  Ready = "READY",
  InProgress = "IN_PROGRESS",
  Closed = "CLOSED",
  Archived = "ARCHIVED",
}

export enum BudgetListTypeQuery {
  Period = "PERIOD",
  Activity = "ACTIVITY",
}

export enum BudgetListPeriodTypeQuery {
  Monthly = "MONTHLY",
  Quarterly = "QUARTERLY",
  Yearly = "YEARLY",
  Custom = "CUSTOM",
}

/** Request body enum values (uppercase) for POST/PATCH */
export enum BudgetScopeApi {
  Branch = "BRANCH",
  Parish = "PARISH",
}

export enum BudgetTypeApi {
  Period = "PERIOD",
  Activity = "ACTIVITY",
}

export enum BudgetPeriodTypeApi {
  Monthly = "MONTHLY",
  Quarterly = "QUARTERLY",
  Yearly = "YEARLY",
  Custom = "CUSTOM",
}

export enum BudgetLineTypeApi {
  Income = "INCOME",
  Expense = "EXPENSE",
}

export enum BudgetLineMatchModeApi {
  AccountAndFund = "ACCOUNT_AND_FUND",
  FundOnly = "FUND_ONLY",
}

/**
 * Normalized API response values (lowercase) for the persisted database status.
 * The persisted (Prisma) form is the UPPER_SNAKE of each value. This is the canonical
 * governed lifecycle — see {@link ./budget-state-machine}.
 */
export enum BudgetStatus {
  Draft = "draft",
  ReadyForReview = "ready_for_review",
  InReview = "in_review",
  ChangesRequested = "changes_requested",
  Approved = "approved",
  Ready = "ready",
  InProgress = "in_progress",
  Closed = "closed",
  Archived = "archived",
}

/**
 * Coarse "phase" of a budget, used only for overview UX gating (so the UI never shows execution
 * states — or misleading "critical / late" health — for a budget that has not started). The precise,
 * user-facing governed state is {@link BudgetStatus}; this is a reduced view of it.
 *
 * Mapping (see apps/api .../utils/budget-lifecycle.ts):
 *   ARCHIVED -> Archived; DRAFT -> Draft;
 *   READY_FOR_REVIEW | IN_REVIEW | CHANGES_REQUESTED -> InReview;
 *   APPROVED | READY -> Upcoming; IN_PROGRESS (within window) -> InProgress;
 *   IN_PROGRESS (window elapsed, not yet closed) -> Ended; CLOSED -> Completed.
 */
export enum BudgetLifecycle {
  Draft = "draft",
  InReview = "in_review",
  Upcoming = "upcoming",
  InProgress = "in_progress",
  /** Window has elapsed but the budget is still open — awaiting a manual close (there is no auto-close). */
  Ended = "ended",
  Completed = "completed",
  Archived = "archived",
}

export enum BudgetScope {
  Branch = "branch",
  Parish = "parish",
}

export enum BudgetType {
  Period = "period",
  Activity = "activity",
}

export enum BudgetPeriodType {
  Monthly = "monthly",
  Quarterly = "quarterly",
  Yearly = "yearly",
  Custom = "custom",
}

export enum BudgetLineType {
  Income = "income",
  Expense = "expense",
}

export enum BudgetLineMatchMode {
  AccountAndFund = "account_and_fund",
  FundOnly = "fund_only",
}

export enum BudgetHealth {
  Healthy = "healthy",
  Warning = "warning",
  Critical = "critical",
  Unknown = "unknown",
}

export enum BudgetWarningKind {
  IncomeBelowTarget = "income_below_target",
  ExpenseAbovePlanned = "expense_above_planned",
  CloseToLimit = "close_to_limit",
  EndingSoon = "ending_soon",
}
